import { useState, useEffect } from 'react';
import { Plus, Car, Image, Save, Trash2, CheckCircle, Upload, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealerPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [dealerInfo, setDealerInfo] = useState(null);
  const [cars, setCars] = useState([]);
  const [showAddCar, setShowAddCar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    dealerName: '', contactPerson: '', email: '', phone: '', 
    password: '', address: '', city: '', province: ''
  });
  const [showRegister, setShowRegister] = useState(false);

  const [carForm, setCarForm] = useState({
    make: '', model: '', variant: '', year: new Date().getFullYear(),
    price: '', mileage: '', fuelType: 'Petrol', transmission: 'Manual',
    color: '', description: '', images: []
  });

  const makes = ['Audi', 'BMW', 'Chevrolet', 'Chery', 'Ford', 'Haval', 'Honda', 'Hyundai', 'Isuzu', 
    'Jaguar', 'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Mazda', 'Mercedes-Benz', 'MG', 'Mitsubishi',
    'Nissan', 'Opel', 'Peugeot', 'Porsche', 'Renault', 'Suzuki', 'Toyota', 'Volkswagen', 'Volvo', 'Other'];
  const provinces = ['Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Mpumalanga', 'North West', 'Limpopo', 'Northern Cape'];
  const fuelTypes = ['Petrol', 'Diesel', 'Hybrid', 'Electric'];
  const transmissions = ['Manual', 'Automatic', 'CVT', 'DCT'];
  const colors = ['White', 'Black', 'Silver', 'Grey', 'Blue', 'Red', 'Brown', 'Green', 'Orange', 'Yellow', 'Other'];
  const years = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    document.title = 'Dealer Portal | List Your Cars | AutoSearch SA';
    // Check if dealer is logged in
    const dealer = localStorage.getItem('dealer');
    if (dealer) {
      setDealerInfo(JSON.parse(dealer));
      setIsLoggedIn(true);
      fetchDealerCars(JSON.parse(dealer).id);
    }
  }, []);

  const fetchDealerCars = async (dealerId) => {
    try {
      const res = await axios.get(`${API}/dealer-portal/cars/${dealerId}`);
      setCars(res.data.cars || []);
    } catch (e) {
      console.error('Failed to fetch cars:', e);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/dealer-portal/login`, loginForm);
      if (res.data.success) {
        localStorage.setItem('dealer', JSON.stringify(res.data.dealer));
        setDealerInfo(res.data.dealer);
        setIsLoggedIn(true);
        fetchDealerCars(res.data.dealer.id);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/dealer-portal/register`, registerForm);
      if (res.data.success) {
        alert('Registration successful! Please login.');
        setShowRegister(false);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const carData = { ...carForm, dealerId: dealerInfo.id, dealerName: dealerInfo.dealerName };
      const res = await axios.post(`${API}/dealer-portal/cars`, carData);
      if (res.data.success) {
        setSubmitted(true);
        setCarForm({
          make: '', model: '', variant: '', year: new Date().getFullYear(),
          price: '', mileage: '', fuelType: 'Petrol', transmission: 'Manual',
          color: '', description: '', images: []
        });
        fetchDealerCars(dealerInfo.id);
        setTimeout(() => { setSubmitted(false); setShowAddCar(false); }, 2000);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to add car');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await axios.delete(`${API}/dealer-portal/cars/${carId}`);
      fetchDealerCars(dealerInfo.id);
    } catch (e) {
      alert('Failed to delete car');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('dealer');
    setIsLoggedIn(false);
    setDealerInfo(null);
    setCars([]);
  };

  // Login/Register Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-md w-full p-8">
          <div className="text-center mb-6">
            <Car className="w-12 h-12 text-blue-600 mx-auto mb-2" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dealer Portal</h1>
            <p className="text-gray-500 dark:text-gray-400">List your cars on AutoSearch SA</p>
          </div>

          {!showRegister ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                <input type="password" required value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Logging in...' : 'Login'}
              </button>
              <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
                Don't have an account? <button type="button" onClick={() => setShowRegister(true)} className="text-blue-600 hover:underline">Register</button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-3">
              <input type="text" required placeholder="Dealership Name" value={registerForm.dealerName} onChange={(e) => setRegisterForm({...registerForm, dealerName: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              <input type="text" required placeholder="Contact Person" value={registerForm.contactPerson} onChange={(e) => setRegisterForm({...registerForm, contactPerson: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              <input type="email" required placeholder="Email" value={registerForm.email} onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              <input type="tel" required placeholder="Phone" value={registerForm.phone} onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              <input type="password" required placeholder="Password" value={registerForm.password} onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              <input type="text" required placeholder="Address" value={registerForm.address} onChange={(e) => setRegisterForm({...registerForm, address: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
              <div className="grid grid-cols-2 gap-2">
                <input type="text" required placeholder="City" value={registerForm.city} onChange={(e) => setRegisterForm({...registerForm, city: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
                <select required value={registerForm.province} onChange={(e) => setRegisterForm({...registerForm, province: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                  <option value="">Province</option>
                  {provinces.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50">
                {loading ? 'Registering...' : 'Register'}
              </button>
              <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
                Already registered? <button type="button" onClick={() => setShowRegister(false)} className="text-blue-600 hover:underline">Login</button>
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Dealer Dashboard
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-blue-600 text-white py-4">
        <div className="container flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">{dealerInfo.dealerName}</h1>
            <p className="text-blue-200 text-sm">{dealerInfo.city}, {dealerInfo.province}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowAddCar(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50">
              <Plus className="w-4 h-4" /> Add Car
            </button>
            <button onClick={handleLogout} className="px-4 py-2 bg-blue-700 rounded-lg hover:bg-blue-800">Logout</button>
          </div>
        </div>
      </div>

      <div className="container py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{cars.length}</div>
            <div className="text-sm text-gray-500">Listed Cars</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-green-600">{cars.filter(c => c.status === 'active').length}</div>
            <div className="text-sm text-gray-500">Active</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-amber-600">{cars.filter(c => c.status === 'pending').length}</div>
            <div className="text-sm text-gray-500">Pending</div>
          </div>
        </div>

        {/* Car Listings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-bold text-gray-900 dark:text-white">Your Car Listings</h2>
          </div>
          {cars.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No cars listed yet. Click "Add Car" to get started.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {cars.map(car => (
                <div key={car.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="h-40 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    {car.images?.[0] ? (
                      <img src={car.images[0]} alt={car.title} className="w-full h-full object-cover" />
                    ) : (
                      <Car className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{car.year} {car.make} {car.model}</h3>
                    <p className="text-lg font-bold text-blue-600">R {car.price?.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">{car.mileage?.toLocaleString()} km • {car.transmission}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${car.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {car.status}
                      </span>
                      <button onClick={() => handleDeleteCar(car.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Car Modal */}
      {showAddCar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Car</h2>
              <button onClick={() => setShowAddCar(false)} className="text-gray-500 hover:text-gray-700"><X className="w-6 h-6" /></button>
            </div>

            {submitted ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Car Added!</h3>
                <p className="text-gray-500">Your listing will appear in search results.</p>
              </div>
            ) : (
              <form onSubmit={handleAddCar} className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Make *</label>
                    <select required value={carForm.make} onChange={(e) => setCarForm({...carForm, make: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                      <option value="">Select Make</option>
                      {makes.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model *</label>
                    <input type="text" required value={carForm.model} onChange={(e) => setCarForm({...carForm, model: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="e.g., Corolla" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year *</label>
                    <select required value={carForm.year} onChange={(e) => setCarForm({...carForm, year: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Price (R) *</label>
                    <input type="number" required value={carForm.price} onChange={(e) => setCarForm({...carForm, price: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="250000" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mileage (km) *</label>
                    <input type="number" required value={carForm.mileage} onChange={(e) => setCarForm({...carForm, mileage: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="50000" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Color</label>
                    <select value={carForm.color} onChange={(e) => setCarForm({...carForm, color: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                      <option value="">Select Color</option>
                      {colors.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fuel Type</label>
                    <select value={carForm.fuelType} onChange={(e) => setCarForm({...carForm, fuelType: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                      {fuelTypes.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transmission</label>
                    <select value={carForm.transmission} onChange={(e) => setCarForm({...carForm, transmission: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                      {transmissions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Variant/Trim</label>
                  <input type="text" value={carForm.variant} onChange={(e) => setCarForm({...carForm, variant: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="e.g., 1.8 XS CVT" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea value={carForm.description} onChange={(e) => setCarForm({...carForm, description: e.target.value})} rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="Full service history, excellent condition..." />
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => setShowAddCar(false)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg">Cancel</button>
                  <button type="submit" disabled={loading} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold disabled:opacity-50">
                    {loading ? 'Adding...' : 'Add Car'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerPortal;
